import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-xs border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90',
        success: 'bg-status-success text-fg-primary [a&]:hover:opacity-90',
        warning:
          'bg-[var(--color-yellow-500)] text-[var(--color-neutral-900)] [a&]:hover:opacity-90',
        outline:
          'border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        ghost: '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        chip: 'rounded-[100rem] bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        // The quieter chip on a sent message's context label. Rendered as a
        // button (a label that opens its lecture) it gains a hover state.
        'chip-muted':
          'rounded-[100rem] bg-secondary leading-4 text-fg-secondary [a&]:hover:bg-secondary/90 [button&]:hover:bg-surface-highlight',
        // A chip that sits on the primary surface (the chat composer): a
        // highlight in light mode, the plain chip colour over glass in dark.
        'chip-highlight':
          'rounded-[100rem] bg-surface-highlight text-fg-primary dark:bg-secondary dark:text-secondary-foreground [a&]:hover:bg-surface-highlight/90 dark:[a&]:hover:bg-secondary/90',
        // The composer's question pill once the chat covers the whole QBank
        // block (ORA-1285): the same chip, tinted with the brand teal.
        'chip-accent':
          'rounded-[100rem] border-green-400/45 bg-green-400/20 text-fg-primary dark:bg-green-400/15',
        'brand-gradient':
          '!border-0 bg-[linear-gradient(90.28deg,#5652ad_6.34%,#c37c86_105%)] text-[var(--color-neutral-25)] [a&]:hover:bg-[linear-gradient(90.28deg,#5652ad_6.34%,#c37c86_105%)]',
      },
      context: {
        default: '',
        modal:
          'bg-[var(--color-neutral-0-10)] text-[var(--color-neutral-25)] [a&]:hover:bg-[var(--color-neutral-0-10)]',
      },
    },
    compoundVariants: [
      {
        variant: 'success',
        context: 'modal',
        className:
          'bg-[var(--color-green-400)] text-fg-primary hover:bg-[var(--color-green-400)] [a&]:hover:bg-[var(--color-green-400)] dark:bg-[var(--color-green-400)]',
      },
      {
        variant: 'destructive',
        context: 'modal',
        className:
          'bg-[var(--color-red-500)] text-white hover:bg-[var(--color-red-500)] [a&]:hover:bg-[var(--color-red-500)] dark:bg-[var(--color-red-500)]',
      },
    ],
    defaultVariants: {
      variant: 'default',
      context: 'default',
    },
  }
);

function Badge({
  className,
  variant = 'default',
  context = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-context={context}
      className={cn(badgeVariants({ variant, context }), className)}
      {...props}
    />
  );
}

export { Badge };
